
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Trash2, Edit } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { currencies, sessionFormats } from '@/utils/profile-options';

interface TherapyModality {
  id: string;
  name_cn: string;
  name_en: string;
}

interface ServiceTherapyTag {
  id: string;
  is_primary: boolean;
  therapy_modality: TherapyModality;
}

interface Service {
  id: string;
  service_name: string;
  description: string | null;
  price: number;
  duration_minutes: number;
  formats_available: string[];
  is_active: boolean;
  currency: string;
  service_photo: string | null;
  is_free_session: boolean;
  created_at: string;
  updated_at: string;
  service_therapy_tags?: ServiceTherapyTag[];
}

interface ServicesTableProps {
  services: Service[];
  onToggleActive: (serviceId: string, currentStatus: boolean) => void;
  onEdit: (service: Service) => void;
  onDelete: (serviceId: string) => void;
}

export const ServicesTable = ({ services, onToggleActive, onEdit, onDelete }: ServicesTableProps) => {
  const { t } = useTranslation('dashboard');

  const formatPrice = (price: number, currency: string) => {
    const currencyInfo = currencies.find(c => c.value === currency);
    const symbol = currencyInfo ? currencyInfo.label.split('(')[1]?.split(')')[0] || '$' : '$';
    return `${symbol}${price.toFixed(2)}`;
  };

  const getFormatDisplayName = (format: string) => {
    const sessionFormat = sessionFormats.find(f => f.value === format);
    return sessionFormat ? sessionFormat.label : format;
  };

  return (
    <div className="overflow-hidden rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('servicesManagement.table.service')}</TableHead>
            <TableHead>{t('servicesManagement.table.price')}</TableHead>
            <TableHead>{t('servicesManagement.table.duration')}</TableHead>
            <TableHead>{t('servicesManagement.table.therapyTags')}</TableHead>
            <TableHead>{t('servicesManagement.table.format')}</TableHead>
            <TableHead>{t('servicesManagement.table.type')}</TableHead>
            <TableHead>{t('servicesManagement.table.status')}</TableHead>
            <TableHead className="text-right">{t('servicesManagement.table.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {services.map((service) => (
            <TableRow key={service.id}>
              <TableCell className="font-medium">{service.service_name}</TableCell>
              <TableCell>{formatPrice(service.price, service.currency)}</TableCell>
              <TableCell>{service.duration_minutes} {t('servicesManagement.table.minutes')}</TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {service.service_therapy_tags && service.service_therapy_tags.length > 0 ? (
                    service.service_therapy_tags
                      .sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0))
                      .map((tag) => (
                        <span 
                          key={tag.id}
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            tag.is_primary 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {tag.is_primary && '★ '}
                          {tag.therapy_modality.name_en}
                        </span>
                      ))
                  ) : (
                    <span className="text-xs text-gray-400">{t('servicesManagement.table.noTags')}</span>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {service.formats_available.map((format) => (
                    <span 
                      key={format} 
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100"
                    >
                      {getFormatDisplayName(format)}
                    </span>
                  ))}
                </div>
              </TableCell>
              <TableCell>
                {service.is_free_session ? (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {t('servicesManagement.table.freeIntern')}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {t('servicesManagement.table.regular')}
                  </span>
                )}
              </TableCell>
              <TableCell>
                <div className="flex items-center">
                  <span 
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      service.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {service.is_active ? t('servicesManagement.table.active') : t('servicesManagement.table.inactive')}
                  </span>
                </div>
              </TableCell>
               <TableCell className="text-right">
                 <div className="flex justify-end gap-2">
                   <Button 
                     variant="outline" 
                     size="icon"
                     onClick={() => onToggleActive(service.id, service.is_active)}
                     title={service.is_active ? t('servicesManagement.table.deactivateService') : t('servicesManagement.table.activateService')}
                   >
                     <Switch checked={service.is_active} className="pointer-events-none" />
                   </Button>
                   <Button 
                     variant="outline" 
                     size="icon"
                     onClick={() => onEdit(service)}
                     title={t('servicesManagement.table.editService')}
                   >
                     <Edit className="h-4 w-4" />
                   </Button>
                   <Button 
                     variant="outline" 
                     size="icon"
                     onClick={() => onDelete(service.id)}
                     title={t('servicesManagement.table.deleteService')}
                   >
                     <Trash2 className="h-4 w-4" />
                   </Button>
                 </div>
               </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
